import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Link, Linkedin, X } from "lucide-react";
import PropTypes from "prop-types";
import { useState } from "react";

// Custom SVG Icon
const TwitterXIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const ShareImageDialog = ({ open, onOpenChange, image }) => {
  const [copied, setCopied] = useState(false);

  // Handle copy link
  const handleCopyLink = async (imageUrl) => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
    }
  };

  // Handle social media sharing
  const handleSocialShare = (platform, imageUrl, prompt) => {
    const text = encodeURIComponent(
      prompt || "Check out this AI-generated image!"
    );
    const url = encodeURIComponent(imageUrl);

    let shareUrl = "";

    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case "reddit":
        shareUrl = `https://reddit.com/submit?url=${url}&title=${text}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  if (!image) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md p-0 bg-[#1a1a1a] border-0 rounded-2xl"
        style={{ zIndex: 9999 }}
        hideCloseButton={true}
      >
        <div className="relative">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-10 h-8 w-8 text-white bg-transparent hover:bg-white/10"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </Button>

          {/* Header */}
          <div className="p-6 pb-4">
            <h2 className="text-xl font-semibold text-white">
              {(image.prompt || image.alt || "Generated Image")
                .split(" ")
                .slice(0, 5)
                .join(" ") +
                ((image.prompt || image.alt || "Generated Image").split(" ")
                  .length > 5
                  ? "..."
                  : "")}
            </h2>
          </div>

          {/* Image */}
          <div className="px-6 pb-6">
            <div className="flex justify-center">
              <img
                src={image.url}
                alt={image.prompt || image.alt || "Generated Image"}
                className="max-w-full max-h-80 object-contain rounded-lg"
              />
            </div>
          </div>

          {/* Share Options */}
          <div className="px-6 pb-6">
            <div className="grid grid-cols-4 gap-4">
              {/* Copy Link */}
              <button
                onClick={() => handleCopyLink(image.url)}
                className="flex items-center justify-center p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-transparent flex items-center justify-center">
                  {copied ? (
                    <span className="text-xs text-white font-medium">
                      Copied
                    </span>
                  ) : (
                    <Link className="h-6 w-6 text-white" />
                  )}
                </div>
              </button>

              {/* Twitter/X */}
              <button
                onClick={() =>
                  handleSocialShare(
                    "twitter",
                    image.url,
                    image.prompt || image.alt
                  )
                }
                className="flex items-center justify-center p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-transparent flex items-center justify-center">
                  <TwitterXIcon className="h-6 w-6 text-white" />
                </div>
              </button>

              {/* LinkedIn */}
              <button
                onClick={() =>
                  handleSocialShare(
                    "linkedin",
                    image.url,
                    image.prompt || image.alt
                  )
                }
                className="flex items-center justify-center p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-transparent flex items-center justify-center">
                  <Linkedin className="h-6 w-6 text-white" />
                </div>
              </button>

              {/* Reddit */}
              <button
                onClick={() =>
                  handleSocialShare(
                    "reddit",
                    image.url,
                    image.prompt || image.alt
                  )
                }
                className="flex items-center justify-center p-3 rounded-lg bg-transparent hover:bg-white/5 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-transparent flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-white"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ShareImageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  image: PropTypes.shape({
    url: PropTypes.string.isRequired,
    prompt: PropTypes.string,
    alt: PropTypes.string,
  }),
};

export default ShareImageDialog;
